import React from 'react';

import styled from '@emotion/styled';

import { Label, Text, Box, FontWeight, COLORS, UnstyledButton } from '@clutter/clean';

import { Timestamp, Picture } from '@shared/components/helpers';
import { ImgixBG, ImgixCrop, ImgixFit } from '@shared/utils/imgix';

import { itemNameText } from '@portal/components/helpers/inventory/item_card';
import { ConditionNotesPhotoGalleryDialog } from '@portal/components/items/condition_notes/condition_notes_photo_gallery_dialog';
import unsafeToReturnErrorIcon from '@portal/images/icons/unsafe_to_return_error.png';
import { CustomerItemFragment, useUsageWithDownsizeQuery, ImageFragment } from '@portal/schema';

/** `PED_BLOCK_GAP`: space between type / location / photos; `PED_HEADING_TO_BODY_GAP`: label to value. */
/** Vertical gap between type / location / photos blocks (same rhythm between each) */
const PED_BLOCK_GAP = '24px';
const PED_HEADING_TO_BODY_GAP = '4px';
/** Tighter space above the first heading inside the grey card */
const PED_CARD_PADDING_TOP = '8px';
const PED_CARD_PADDING_BOTTOM = '24px';
const PED_THUMB_GRID_GAP = '16px';

const pedLabelCss = { margin: 0, display: 'block' as const };
const pedValueCss = { margin: 0 };

const Separator = styled.hr`
  background: ${COLORS.grayBorder};
  border: none;
  margin: 24px 0;
  height: 1px;
`;

const LineItem: React.FC<{ label: React.ReactNode; content: React.ReactNode }> = ({ label, content }) => (
  <React.Fragment>
    <Separator />
    <Box.Flex justifyContent="space-between">
      <Text.Body weight={FontWeight.Medium}>{label}</Text.Body>
      <span> {content}</span>
    </Box.Flex>
  </React.Fragment>
);

const PED_THUMB = 96;

/** Fixed 96×96 tile; tap opens `ConditionNotesPhotoGalleryDialog` (same as condition notes page). */
const ConditionNotesPhoto: React.FC<{
  source: string;
  imageIndex: number;
  imageCount: number;
  itemDisplayName: string;
  onOpen(): void;
}> = ({ source, imageIndex, imageCount, itemDisplayName, onOpen }) => {
  const [failed, setFailed] = React.useState(false);
  const shortAlt = `Condition photo for ${itemDisplayName} (${imageIndex + 1} of ${imageCount})`;
  const openLabel = `View larger — ${shortAlt}`;

  const inner = failed ? (
    <Box
      width={`${PED_THUMB}px`}
      height={`${PED_THUMB}px`}
      background={COLORS.cloud}
      border={`1px solid ${COLORS.grayBorder}`}
      borderRadius="2px"
      overflow="hidden"
      role="img"
      aria-hidden
      css={{ lineHeight: 0 }}
    >
      <Picture
        width={PED_THUMB}
        height={PED_THUMB}
        background={ImgixBG.White}
        fit={ImgixFit.Crop}
        crop={ImgixCrop.Focalpoint}
        alt=""
      />
    </Box>
  ) : (
    <Box
      width={`${PED_THUMB}px`}
      height={`${PED_THUMB}px`}
      border={`1px solid ${COLORS.grayBorder}`}
      borderRadius="2px"
      overflow="hidden"
      title={shortAlt}
      css={{
        lineHeight: 0,
        pointerEvents: 'none',
        '& img': {
          display: 'block',
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        },
      }}
    >
      <Picture
        source={source}
        width={PED_THUMB}
        height={PED_THUMB}
        background={ImgixBG.White}
        fit={ImgixFit.Crop}
        crop={ImgixCrop.Focalpoint}
        alt=""
        onError={() => setFailed(true)}
      />
    </Box>
  );

  return (
    <UnstyledButton
      type="button"
      onClick={onOpen}
      aria-label={openLabel}
      css={{
        display: 'block',
        padding: 0,
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        borderRadius: '2px',
        flexShrink: 0,
        lineHeight: 0,
        width: `${PED_THUMB}px`,
        height: `${PED_THUMB}px`,
      }}
    >
      {inner}
    </UnstyledButton>
  );
};

export const Info: React.FC<{ item: CustomerItemFragment }> = ({ item }) => {
  const { data } = useUsageWithDownsizeQuery();
  const [conditionGallery, setConditionGallery] = React.useState<{
    images: ImageFragment[];
    start: number;
  } | null>(null);

  React.useEffect(() => {
    if (conditionGallery) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
    return undefined;
  }, [conditionGallery]);

  if (!data) return null;

  const itemCuft = Math.round(item.cuft || 1) || 1;
  const totalCuft = data.account.usage.total;
  const cuftPct =
    !totalCuft || totalCuft <= 0 || !Number.isFinite(totalCuft)
      ? 0
      : Math.min(100, Math.round((itemCuft / totalCuft) * 100));
  const ped = 'beforePickupDamageDocumentation' in item ? item.beforePickupDamageDocumentation : undefined;
  const damaged = 'afterPickupDamageDocumentation' in item ? item.afterPickupDamageDocumentation : undefined;
  const conditionBlocks: Array<{
    key: string;
    typeLabel: string;
    locationLabel: string;
    docs: NonNullable<typeof ped>;
  }> = [];
  if (ped && (ped.damageTypeLabels.length > 0 || ped.damageLocationLabels.length > 0 || ped.images.length > 0)) {
    conditionBlocks.push({
      key: 'before-pickup',
      typeLabel: 'Item condition type',
      locationLabel: 'Item condition location',
      docs: ped,
    });
  }
  if (
    damaged &&
    (damaged.damageTypeLabels.length > 0 ||
      damaged.damageLocationLabels.length > 0 ||
      damaged.images.length > 0 ||
      damaged.unsafeToReturn)
  ) {
    conditionBlocks.push({
      key: 'after-pickup',
      typeLabel: 'Damage type',
      locationLabel: 'Damage location',
      docs: damaged,
    });
  }

  return (
    <>
      {conditionBlocks.map((block) => {
        const docs = block.docs;
        const hasTypeOrLocation = docs.damageTypeLabels.length > 0 || docs.damageLocationLabels.length > 0;
        const showUnsafeToReturn = block.key === 'after-pickup' && docs.unsafeToReturn;
        return (
          <Box
            key={block.key}
            background={COLORS.grayBackground}
            borderRadius="4px"
            padding={`${PED_CARD_PADDING_TOP} 0 ${PED_CARD_PADDING_BOTTOM}`}
            margin="0 0 16px"
            css={{ boxSizing: 'border-box', textAlign: 'left', width: '100%' }}
          >
            <Box.Flex flexDirection="column" gap={PED_BLOCK_GAP} width="100%" alignItems="stretch">
              {hasTypeOrLocation && (
                <Box.Flex flexDirection="column" gap={PED_BLOCK_GAP} width="100%" alignItems="flex-start">
                  {docs.damageTypeLabels.length > 0 && (
                    <Box.Flex flexDirection="column" gap={PED_HEADING_TO_BODY_GAP} width="100%" alignItems="flex-start">
                      <Label as="div" css={pedLabelCss}>
                        {block.typeLabel}
                      </Label>
                      <Text.Callout color={COLORS.hippo} weight={FontWeight.Medium} css={pedValueCss}>
                        {docs.damageTypeLabels.join(', ')}
                      </Text.Callout>
                    </Box.Flex>
                  )}
                  {docs.damageLocationLabels.length > 0 && (
                    <Box.Flex flexDirection="column" gap={PED_HEADING_TO_BODY_GAP} width="100%" alignItems="flex-start">
                      <Label as="div" css={pedLabelCss}>
                        {block.locationLabel}
                      </Label>
                      <Text.Callout color={COLORS.hippo} weight={FontWeight.Medium} css={pedValueCss}>
                        {docs.damageLocationLabels.join(', ')}
                      </Text.Callout>
                    </Box.Flex>
                  )}
                </Box.Flex>
              )}

              {showUnsafeToReturn && (
                <Box.Flex alignItems="center" gap="6px" padding="6px 0" css={{ color: '#be0706' }}>
                  <img src={unsafeToReturnErrorIcon} alt="" width={23} height={23} aria-hidden />
                  <Text.Body weight={FontWeight.Medium} css={{ color: '#be0706' }}>
                    Unsafe to return
                  </Text.Body>
                </Box.Flex>
              )}
              {docs.images.length > 0 && (
                <Box.Flex flexDirection="column" gap={PED_HEADING_TO_BODY_GAP} width="100%" alignItems="flex-start">
                  <Label as="div" css={pedLabelCss}>
                    Condition photos
                  </Label>
                  <Box.Grid
                    width="100%"
                    gridTemplateColumns={`repeat(auto-fill, minmax(${PED_THUMB}px, 1fr))`}
                    gridGap={PED_THUMB_GRID_GAP}
                    css={{ justifyItems: 'start', lineHeight: 0 }}
                  >
                    {docs.images.map((img, i) => (
                      <ConditionNotesPhoto
                        key={img.id}
                        source={img.source}
                        imageIndex={i}
                        imageCount={docs.images.length}
                        itemDisplayName={itemNameText(item) ?? 'Item'}
                        onOpen={() => setConditionGallery({ images: docs.images, start: i })}
                      />
                    ))}
                  </Box.Grid>
                </Box.Flex>
              )}
            </Box.Flex>
          </Box>
        );
      })}
      <Separator />
      <Box margin="0">
        <Label>{itemCuft} cubic feet</Label>
        <Text.Callout>This item takes up {cuftPct}% of your total storage space</Text.Callout>
        {'barcode' in item && <LineItem label="Item barcode" content={item.barcode.value} />}
        {item.pickedUpAt && (
          <LineItem
            label="Pickup date"
            content={<Timestamp value={item.pickedUpAt} format={Timestamp.Format.Date} />}
          />
        )}
        {item.returnedAt && (
          <LineItem
            label="Returned date"
            content={<Timestamp value={item.returnedAt} format={Timestamp.Format.Date} />}
          />
        )}
      </Box>
      <Separator />
      <ConditionNotesPhotoGalleryDialog
        open={!!conditionGallery}
        images={conditionGallery?.images ?? []}
        startIndex={conditionGallery?.start ?? 0}
        onClose={() => setConditionGallery(null)}
      />
    </>
  );
};
